import type { ReactNode } from 'react';
import { Surface } from './Surface';
import type { SurfaceVariant } from './Surface';
import { Cluster, Stack } from './Layout';

// ------------------------------------------------------------------
// Card / CardHeader / CardBody / CardFooter — story 2-147ta (P10 §2.7 row 5,
// DOD). The full slot family closing the same named gap `Surface.tsx`
// closes for the base visual treatment — `Card` is `Surface` (padding
// `'lg'`, the p-5 collection-card convention) plus the vertical-rhythm
// contract every real hand-rolled card in the tree already reaches for by
// hand (`TemplateCard.tsx`'s `space-y-3`, `KpiCard.tsx`'s `gap-4`,
// `GallerySection.tsx`'s `mt-4`).
//
// Built ENTIRELY from `2-147sa`'s layout primitives internally (`Stack`/
// `Cluster`) — no bespoke `flex`/`grid` className anywhere in this file, so
// this story adds zero new debt to `check-layout-through-primitives-
// ceiling.mjs`. Every action area composes `2-147r`'s `CardActions` (see
// `CardHeaderProps.actions`/`CardFooterProps.children`'s own doc comments)
// — this file never renders a raw `<button>` itself.
//
// THREE SLOTS, NOT A FIXED HEADER/BODY/FOOTER SHAPE. A caller composes
// whichever slots the content needs (`MetricCard.tsx` uses bare `Card` with
// no slots at all — its own layout doesn't map onto title/body/footer;
// `EntityCard.tsx` uses all three) — `Card` itself only supplies the
// surface + vertical rhythm, exactly like `Layout.tsx`'s `Section` supplies
// heading rhythm without dictating what the caller puts inside it.
// ------------------------------------------------------------------

export type CardVariant = SurfaceVariant;

// Local Tag alias — `Surface.tsx`'s own convention (one small alias per
// file rather than a shared cross-file type-only import).
type Tag = 'div' | 'article' | 'section' | 'li';

export interface CardProps {
  /** Defaults to `'flat'` — P10 §1.3's own default surface role for "this looks like a card." `'glass'` is reserved for primary-action/status/active-AI-work cards only (§1.3's own test) — a caller reaching for it should be able to answer "is this the primary action on the screen, a status the whole screen is about, or active AI work happening right now?" with yes. */
  variant?: CardVariant;
  /** Defaults to `'article'` — a card is a self-contained content unit (matches `TaskCard.tsx`'s/`TemplateCard.tsx`'s own existing element choice). */
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  children: ReactNode;
}

export function Card({ variant = 'flat', as = 'article', className = '', 'data-testid': testId, children }: CardProps): JSX.Element {
  return (
    <Surface as={as} variant={variant} padding="lg" className={className} {...(testId ? { 'data-testid': testId } : {})}>
      <Stack gap={4}>{children}</Stack>
    </Surface>
  );
}

// ------------------------------------------------------------------
// CardHeader — title + optional subtitle/badges, trailing actions. Same
// responsive "stack under the identity block, row alongside it at `sm`+"
// idiom `Layout.tsx`'s own `Section` heading row and `Headers.tsx`'s
// `WorkspaceHeader` already establish (`Cluster justify="between"
// align="start"`) — not a new shape, the THIRD real consumer of it.
// ------------------------------------------------------------------
export interface CardHeaderProps {
  title: ReactNode;
  /** Renders inside a `<div>`, not a `<p>` — `subtitle` is typed `ReactNode` (not `string`, unlike `TaskCard`'s own subtitle) so a caller can pass a loading-state `Skeleton` here (this story's own "EntityCard — loading" gallery state does exactly that); a `<p>` wrapper would put a block-level `<div>` inside phrasing content, a real `validateDOMNesting` warning caught live building that demo. */
  subtitle?: ReactNode;
  /** A wrapping row of status/origin badges under the title — `StatusChip`/`Badge`, never rendered raw here (this slot only lays them out). */
  badges?: ReactNode;
  /** Trailing action area — compose `2-147r`'s `CardActions` here, never a raw `Button` list (this is the exact escape hatch this story's own done-when forbids reinventing). */
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, badges, actions, className = '' }: CardHeaderProps): JSX.Element {
  return (
    <Cluster justify="between" align="start" gap={4} className={className}>
      {/*
        `min-w-0` alone lets this flex item SHRINK below its content size,
        but `Cluster`'s `flex-wrap` decides which line an item goes on using
        each item's HYPOTHETICAL (pre-shrink, `flex-basis: auto` = content)
        size — with a long `title` + a real `actions` control, the title's
        own content width already exceeds the row, so the actions item wraps
        to its own line UNDER the title instead of sharing the row (found
        live: a long-titled `TemplateCard` — story 2-147wb — rendered its
        `OverflowMenu` trigger ~80px below the title instead of beside it).
        `flex-1` (flex-basis: 0%) fixes the ROOT CAUSE, not just this one
        consumer: it makes this item's hypothetical size zero, so the wrap
        decision is based on `actions`' own fixed width instead, and
        `min-w-0` (kept) still lets it shrink/truncate normally once space
        is allocated. Safe for every existing consumer — this was already
        meant to be the flexible partner of `actions`' `shrink-0` fixed
        width; `flex-1` makes that intent hold at every content length,
        it does not change behavior when there IS enough room.
      */}
      <Stack gap={1.5} className="min-w-0 flex-1">
        <h3 className="truncate text-lg font-semibold text-foreground">{title}</h3>
        {subtitle != null && <div className="text-sm text-muted-foreground">{subtitle}</div>}
        {badges != null && <Cluster gap={2}>{badges}</Cluster>}
      </Stack>
      {actions != null && <div className="shrink-0">{actions}</div>}
    </Cluster>
  );
}

// ------------------------------------------------------------------
// CardBody — the content slot. Pure passthrough with the card's own
// `text-sm text-muted-foreground` body-copy default (a caller rendering
// `text-foreground` prose overrides per-element, same as every other DS
// content slot).
// ------------------------------------------------------------------
export interface CardBodyProps {
  children: ReactNode;
  className?: string;
}

export function CardBody({ children, className = '' }: CardBodyProps): JSX.Element {
  return <div className={`text-sm text-muted-foreground ${className}`.trim()}>{children}</div>;
}

// ------------------------------------------------------------------
// CardFooter — usually `CardActions` (trailing edge, the same `align="end"`
// default that host already uses) or metadata. `divider` renders the
// hairline top border that separates it from the content above — the same
// `border-t border-border/60` `TaskCard.tsx`'s own notice footer already
// uses — defaulting to `true` since a footer is almost always visually
// distinct from the body; a footer that IS the card's only content (no
// header/body above it) passes `divider={false}`.
// ------------------------------------------------------------------
export interface CardFooterProps {
  children: ReactNode;
  /** Defaults to `'end'` (trailing edge — `CardActions`' own default). `'start'` matches `AgentPlanCard`'s left-aligned Approve/Decline footer, where actions read left-to-right like a sentence. */
  justify?: 'start' | 'end';
  divider?: boolean;
  className?: string;
}

export function CardFooter({ children, justify = 'end', divider = true, className = '' }: CardFooterProps): JSX.Element {
  return (
    <Cluster
      justify={justify}
      gap={2}
      className={`${divider ? 'border-t border-border/60 pt-4' : ''} ${className}`.trim()}
    >
      {children}
    </Cluster>
  );
}
